/**
 * Draft Store - Local persistence for offline/resume
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { CandidateAnswer } from '../types/exam';

interface DraftState {
  drafts: Record<
    string,
    {
      candidateExamId: string;
      formId: string;
      answers: CandidateAnswer[];
      lastSaved: number;
      serverTime: number;
    }
  >;

  saveDraft: (
    candidateExamId: string,
    formId: string,
    answers: CandidateAnswer[],
    serverTime: number
  ) => void;
  getDraft: (candidateExamId: string) => DraftState['drafts'][string] | null;
  clearDraft: (candidateExamId: string) => void;
  clearOldDrafts: (maxAgeMs: number) => void;
}

export const useDraftStore = create<DraftState>()(
  persist(
    (set, get) => ({
      drafts: {},

      saveDraft: (candidateExamId, formId, answers, serverTime) => {
        set(state => ({
          drafts: {
            ...state.drafts,
            [candidateExamId]: {
              candidateExamId,
              formId,
              answers,
              lastSaved: Date.now(),
              serverTime,
            },
          },
        }));
      },

      getDraft: candidateExamId => {
        return get().drafts[candidateExamId] || null;
      },

      clearDraft: candidateExamId => {
        set(state => {
          const { [candidateExamId]: _, ...rest } = state.drafts;
          return { drafts: rest };
        });
      },

      clearOldDrafts: maxAgeMs => {
        const now = Date.now();
        set(state => {
          const drafts = Object.entries(state.drafts).reduce(
            (acc, [id, draft]) => {
              if (now - draft.lastSaved < maxAgeMs) {
                acc[id] = draft;
              }
              return acc;
            },
            {} as DraftState['drafts']
          );
          return { drafts };
        });
      },
    }),
    {
      name: 'vsat-exam-drafts',
      version: 1,
    }
  )
);
